import { currentLimits, limitCanAddCollaborator, safeLimits } from '#~/api/billing/limits.js';
import { toHexFromId, toIdFromHex } from '#~/api/utils.js';
import { useAuth0 } from '#~/auth/index.js';
import { useVerifiedAction } from '#~/auth/email-verification-gate/use-verified-action.jsx';
import { config } from '#~/config/config.js';
import { nats } from '#~/nats/index.js';
import { post } from '#~/notifications/index.js';
import {
  useAnalytics,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  SHARING_PROPERTIES
} from '#~/analytics/index.js';
import { normalizeEmail, emailsEqual } from '#~/utils/email.js';
import { LimitExceeded } from './components.jsx';
import { ReverseRoleTranslationMap, RoleTranslationMap } from './constants.js';
import { UserInviteDetails } from './invite-details.jsx';
import { InviteByLink } from './invite-link.jsx';
import { GuestsTab } from './tab-guests.jsx';
import type { User } from './types.js';
import { Button } from '@studio/design-system/button';
import { AnimatePresence, motion, Motions } from '@studio/design-system/motion';
import { MultiSelect } from '@studio/design-system/multi-select';
import { usePrompt } from '@studio/design-system/prompt';
import * as Select from '@studio/design-system/select';
import { Content, List, Root, Trigger } from '@studio/design-system/tabs';
import T from '@studio/design-system/themes';
import type {
  AgentRegistryListInvitesResponse,
  AgentRegistryMembership
} from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import {
  AgentRegistryCreateInvite,
  AgentRegistryDestroyInvite,
  AgentRegistryDestroyMembership,
  AgentRegistryUpdateInvite,
  AgentRegistryUpdateMembership
} from '@tryiris-ai/mailroom-helper/agentRegistry';
import { clsx } from 'cn';
import { useEffect, useMemo, useState } from 'react';
import { InviteSupport, SupportEmails } from './invite-support.js';
import { getCurrentVendor } from '@studio/design-system/themes/vendor.js';

const valueMap: Record<string, string> = {
  viewer: 'Viewer',
  producer: 'Producer',
  admin: 'Admin'
};

type EmailsInputProps = {
  disabled?: boolean;
  maxUsers?: number;
  currentUsersCount?: number;
  onAdd: (users: User[]) => void;
  /** Runs the send now, or holds it until the sender verifies their email. */
  runVerified: (proceed: () => unknown) => Promise<void>;
};

/** EmailsInput. */
const EmailsInput = ({
  disabled,
  maxUsers,
  onAdd,
  currentUsersCount,
  runVerified
}: EmailsInputProps) => {
  const [emails, setEmails] = useState<{ label: string; value: string }[]>([]);
  const { track } = useAnalytics();

  const canSendInvite =
    !disabled && emails.length !== 0 && limitCanAddCollaborator(currentUsersCount ?? 0);

  const [permission, setPermission] = useState<User['permission']>('viewer');

  useEffect(() => {
    const fn = async () => {
      const limits = await safeLimits();
      !limits?.limits?.canSetCollaboratorPermissions && setPermission('admin');
    };

    fn();
  });

  return (
    <div className='flex items-center gap-x-24'>
      <div className='m-[1px] flex grow text-muted'>
        <MultiSelect
          className={clsx('grow text-left', {
            'cursor-not-allowed': disabled
          })}
          disabled={!!disabled}
          maxItems={maxUsers}
          onChange={setEmails}
          onMaxItemsExceeded={() => {
            // Inline validation at the email field: the feedback is already at
            // the point of action, so it must not also light the bell.
            post(
              {
                title: "Can't add more collaborators",
                message: "You've reached the maximum number of collaborators",
                severity: 'error'
              },
              { autoClose: true }
            );
          }}
          placeholder='Enter email addresses'
          right={
            <>
              {currentLimits?.limits?.canSetCollaboratorPermissions && (
                <Select.Root
                  onValueChange={(v) => {
                    if (currentLimits?.limits?.canSetCollaboratorPermissions === false) {
                      return;
                    }

                    const newPermission = v as User['permission'];
                    setPermission(newPermission);

                    // Track selected permission
                    track(EVENT_NAMES.SELECT_PERMISSION_TYPE, EVENT_CATEGORIES.SHARING, {
                      [SHARING_PROPERTIES.PERMISSION_TYPE]: newPermission
                    });
                  }}
                  value={permission}
                  variant='light'
                >
                  <Select.Trigger
                    className='w-auto bg-transparent pr-0 text-black'
                    disabled={disabled}
                  >
                    {valueMap[permission]}
                  </Select.Trigger>
                  <Select.Content align='end' position='popper' side='bottom'>
                    <Select.Item className='h-auto' value='admin'>
                      <div className='flex flex-col gap-4 py-8'>
                        <Select.ItemText>Admin</Select.ItemText>
                        <div className='text-md text-muted'>
                          Can control all sources & invite users
                        </div>
                      </div>
                    </Select.Item>
                    <Select.Item className='h-auto' value='producer'>
                      <div className='flex flex-col gap-4 py-8'>
                        <Select.ItemText>Producer</Select.ItemText>
                        <div className='text-md text-muted'>
                          Can control all or specific sources
                        </div>
                      </div>
                    </Select.Item>

                    <Select.Item className='h-auto' value='viewer'>
                      <div className='flex flex-col gap-4 py-8'>
                        <Select.ItemText>Viewer</Select.ItemText>
                        <div className='text-md text-muted'>
                          Can view all sources, but not control
                        </div>
                      </div>
                    </Select.Item>
                  </Select.Content>
                </Select.Root>
              )}
            </>
          }
          validateInput={(v) =>
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
              v
            )
          }
          value={emails}
        />
      </div>
      <Button
        className={clsx('mr-2 py-20', {
          'cursor-not-allowed': disabled
        })}
        color='primary'
        disabled={!canSendInvite}
        onClick={async () => {
          if (!canSendInvite) {
            return;
          }

          // The whole send is held while unverified, so the typed addresses stay put until it runs.
          await runVerified(async () => {
            // Normalize email addresses to ensure case-insensitive matching
            const invites = emails.map((email) => ({
              id: '',
              email: normalizeEmail(email.value),
              permission,
              status: 'pending' as User['status']
            }));

            setEmails([]);

            if (currentLimits?.limits?.canSetCollaboratorPermissions) {
              setPermission('viewer');
            }

            await onAdd(invites);

            post({
              message: `Invitation successfully sent to "${emails
                .map((email) => email.value)
                .join('", "')}"`,
              title: 'Invitation sent!',
              severity: 'info'
            });
          });
        }}
        size='lg'
      >
        Send Invite
      </Button>
    </div>
  );
};

/** UserInvite. */
export const UserInvite = ({
  organizationId,
  roomId,
  roomInviteRecords,
  roomMemberRecords,
  onClose
}: {
  organizationId: Uint8Array;
  roomId: Uint8Array;
  roomInviteRecords: AgentRegistryListInvitesResponse['invites'];
  roomMemberRecords: AgentRegistryMembership[];
  onClose: () => void;
}) => {
  const prompt = usePrompt();
  const [COLLABORATOR_LIMIT, setCollaboratorLimit] = useState(0);

  useEffect(() => {
    const fn = async () => {
      const limit = (await safeLimits())?.limits?.maxCollaborators ?? 0;
      setCollaboratorLimit(limit);
    };

    fn();
  }, [setCollaboratorLimit]);

  const { getAccessTokenSilently } = useAuth0();
  const { track } = useAnalytics();
  // Sending an invite needs a verified email. Removing or editing members does not.
  const verifiedAction = useVerifiedAction('invite');

  const [users, setUsers] = useState<User[]>([]);

  const [openInvite, setOpenInvite] = useState<AgentRegistryListInvitesResponse['invites'][0]>();

  const isSupportUser = (user: User) => {
    // Use case-insensitive email comparison for support user detection
    return emailsEqual(user.email, SupportEmails[getCurrentVendor()]);
  };

  const handleInvite = useMemo(
    () => async (newUsers: User[]) => {
      const nextNewUsers: User[] = [];

      for (const user of newUsers) {
        const token = await getAccessTokenSilently();

        const domain = new URL(config.frontendUrl).hostname;
        // Ensure email is normalized for consistent backend processing
        const normalizedEmail = normalizeEmail(user.email);

        const invite = await AgentRegistryCreateInvite.request(nats.cloudConnection(), {
          token,
          organizationId,
          roomId,
          inviteeEmail: normalizedEmail,
          role: ReverseRoleTranslationMap[user.permission],
          domain
        });

        // Track inviting a user (use normalized email for consistency)
        const studioIdStr = toHexFromId(roomId);
        if (studioIdStr) {
          track(EVENT_NAMES.COLLAB_INVITE_USER, EVENT_CATEGORIES.COLLABORATION, {
            studioId: studioIdStr,
            [SHARING_PROPERTIES.INVITEE_EMAIL]: normalizedEmail,
            [SHARING_PROPERTIES.INVITEE_ROLE]: user.permission
          });
        } else {
          track(EVENT_NAMES.COLLAB_INVITE_USER, EVENT_CATEGORIES.COLLABORATION, {
            inviteeEmail: normalizedEmail,
            inviteeRole: user.permission
          });
        }

        nextNewUsers.push({
          ...user,
          id: toHexFromId(invite.id)!,
          email: normalizedEmail, // Use normalized email for consistency
          status: 'pending'
        });
      }

      // Use case-insensitive email comparison for duplicate detection
      setUsers((draft) => {
        const allUsers = [...draft, ...nextNewUsers];
        const uniqueUsers: User[] = [];
        const seenEmails = new Set<string>();

        for (const user of allUsers) {
          const normalizedUserEmail = normalizeEmail(user.email);
          if (!seenEmails.has(normalizedUserEmail)) {
            seenEmails.add(normalizedUserEmail);
            uniqueUsers.push({
              ...user,
              email: normalizedUserEmail // Ensure stored email is normalized
            });
          }
        }

        return uniqueUsers;
      });
    },
    [setUsers, getAccessTokenSilently]
  );

  const handleCreateInviteLink = async (permission: User['permission']): Promise<string> => {
    const token = await getAccessTokenSilently();

    const domain = new URL(config.frontendUrl).hostname;

    const invite = await AgentRegistryCreateInvite.request(nats.cloudConnection(), {
      token,
      organizationId,
      roomId,
      role: ReverseRoleTranslationMap[permission],
      openInvite: true,
      domain
    });

    // Track creating an invite link
    const studioIdStr = toHexFromId(roomId);
    if (studioIdStr) {
      track(EVENT_NAMES.COLLAB_CREATE_LINK, EVENT_CATEGORIES.COLLABORATION, {
        studioId: studioIdStr,
        inviteeRole: permission
      });
    } else {
      track(EVENT_NAMES.COLLAB_CREATE_LINK, EVENT_CATEGORIES.COLLABORATION, {
        inviteeRole: permission
      });
    }

    setOpenInvite(invite);

    return `https://studio.tryiris.com/invite/${toHexFromId(invite.id)!}`;
  };

  const handleDestroyInviteLink = async () => {
    const token = await getAccessTokenSilently();

    if (!openInvite) {
      return;
    }

    await AgentRegistryDestroyInvite.request(nats.cloudConnection(), {
      token,
      id: openInvite.id
    });

    setOpenInvite(undefined);
  };

  const handleResendInvite = async (user: User) => {
    // Track resending an invitation (ensure normalized email for consistency)
    const normalizedEmail = normalizeEmail(user.email);
    const studioIdStr = toHexFromId(roomId);
    if (studioIdStr) {
      track(EVENT_NAMES.RESEND_INVITE, EVENT_CATEGORIES.SHARING, {
        studioId: studioIdStr,
        [SHARING_PROPERTIES.INVITEE_EMAIL]: normalizedEmail,
        [SHARING_PROPERTIES.INVITEE_ROLE]: user.permission
      });
    } else {
      track(EVENT_NAMES.RESEND_INVITE, EVENT_CATEGORIES.SHARING, {
        [SHARING_PROPERTIES.INVITEE_EMAIL]: normalizedEmail,
        [SHARING_PROPERTIES.INVITEE_ROLE]: user.permission
      });
    }

    await handleRemoveUser(user);

    await handleInvite([user]);
  };

  const handleRemoveUser = async (user: User) => {
    const token = await getAccessTokenSilently();

    // Track removing a user (ensure normalized email for consistency)
    const normalizedEmail = normalizeEmail(user.email);
    const studioIdStr = toHexFromId(roomId);
    if (studioIdStr) {
      track(EVENT_NAMES.COLLAB_REMOVE_USER, EVENT_CATEGORIES.COLLABORATION, {
        studioId: studioIdStr,
        inviteeEmail: normalizedEmail,
        inviteeRole: user.permission
      });
    } else {
      track(EVENT_NAMES.COLLAB_REMOVE_USER, EVENT_CATEGORIES.COLLABORATION, {
        inviteeEmail: normalizedEmail,
        inviteeRole: user.permission
      });
    }

    await (user.status === 'pending'
      ? AgentRegistryDestroyInvite.request(nats.cloudConnection(), {
          token,
          id: toIdFromHex(user.id)!
        })
      : AgentRegistryDestroyMembership.request(nats.cloudConnection(), {
          token,
          id: toIdFromHex(user.id)!
        }));

    setUsers((draft) => draft.filter((u) => u.id !== user.id));
  };

  const handleUpdateUser = async (update: Partial<User>, user: User) => {
    const token = await getAccessTokenSilently();
    const exp = update.expiration;

    const isInvite = user.status === 'pending';

    if (isInvite) {
      await AgentRegistryUpdateInvite.request(nats.cloudConnection(), {
        token,
        id: toIdFromHex(user.id)!,
        membershipExpiresAt: exp !== undefined ? Number(exp) / 1000 : undefined,
        role: ReverseRoleTranslationMap[update.permission ?? user.permission]
      });
    } else {
      const role = ReverseRoleTranslationMap[update.permission ?? user.permission];

      await AgentRegistryUpdateMembership.request(nats.cloudConnection(), {
        token,
        id: toIdFromHex(user.id)!,
        membershipExpiresAt: exp !== undefined ? Number(exp) / 1000 : undefined,
        role
      });
    }

    if (exp && Number(exp) === 0) {
      update.expiration = undefined;
    }

    setUsers((draft) => draft.map((u) => (u.id === user.id ? { ...u, ...update } : u)));
  };

  useEffect(() => {
    const fn = async () => {
      const members: AgentRegistryMembership[] = [];

      const userIDMap = Object.fromEntries(members.map((member) => [toHexFromId(member.id), true]));

      roomMemberRecords.forEach((member) => {
        if (userIDMap[toHexFromId(member.id)!]) {
          return;
        }

        userIDMap[toHexFromId(member.id)!] = true;
        members.push(member);
      });

      const users = members.map((member) => ({
        id: toHexFromId(member.id)!,
        email: normalizeEmail(member.userLabel),
        permission: RoleTranslationMap[member.role] ?? 'producer',
        status: 'accepted' as User['status'],
        profilePhotoUrl: member.userProfilePhotoUrl,
        expiration: member.expiresAt ? new Date(member.expiresAt * 1000) : undefined
      }));

      const invites: AgentRegistryListInvitesResponse['invites'] = [];

      roomInviteRecords.forEach((invite) => {
        if (userIDMap[toHexFromId(invite.id)!]) {
          return;
        }

        if (invite.openInvite) {
          setOpenInvite(invite);

          return;
        }

        userIDMap[toHexFromId(invite.id)!] = true;
        invites.push(invite);
      });

      const inviteUsers = invites.map((invite) => ({
        id: toHexFromId(invite.id)!,
        email: normalizeEmail(invite.inviteeEmail ?? invite.userLabel),
        expiration: invite.membershipExpiresAt
          ? new Date(invite.membershipExpiresAt * 1000)
          : undefined,
        permission: RoleTranslationMap[invite.role] ?? 'producer',
        status: 'pending' as User['status'],
        profilePhotoUrl: invite.userProfilePhotoUrl
      }));

      setUsers([...users, ...inviteUsers]);
    };

    fn();
  }, [setUsers, roomMemberRecords, roomInviteRecords]);

  const collaboratorLimitExceeded = users.length >= COLLABORATOR_LIMIT;

  const guests = users.filter((v) => !!v.expiration);

  const usersCount = users.length;

  const emailInputMemo = useMemo(() => {
    return (
      <EmailsInput
        disabled={collaboratorLimitExceeded}
        maxUsers={COLLABORATOR_LIMIT - usersCount}
        currentUsersCount={usersCount}
        onAdd={async (value: User[]) => {
          await handleInvite(value);
        }}
        runVerified={verifiedAction.run}
      />
    );
  }, [collaboratorLimitExceeded, usersCount, COLLABORATOR_LIMIT, handleInvite, verifiedAction.run]);

  const supportUser = useMemo(() => {
    return users.find((user) => isSupportUser(user));
  }, [users]);

  return (
    <div
      key='user-invite-container'
      className='h-screen max-h-[min(686px,calc(100vh-60px))] w-[530px] rounded-12 border-t-12 border-primary bg-light p-32 pt-12'
    >
      <Root data-theme={T.getTheme('light')} defaultValue='users'>
        <List className='px-0'>
          <Trigger value='users'>Invite users</Trigger>
          <Trigger value='guests'>Guests</Trigger>
        </List>
        <div className='mt-12 h-full font-medium text-black'>
          <Content value='users'>
            {emailInputMemo}
            <div className='mt-18 flex flex-col gap-12'>
              <AnimatePresence>
                {!!collaboratorLimitExceeded && (
                  <motion.div {...Motions.Fade}>
                    <LimitExceeded
                      className='mb-4 cursor-pointer'
                      onClick={() => {
                        onClose();
                      }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <InviteByLink
                onCreateLink={handleCreateInviteLink}
                onDestroyLink={handleDestroyInviteLink}
                openInvite={openInvite}
                runVerified={verifiedAction.run}
              />
              {users
                .filter((user) => !isSupportUser(user))
                .map((user) => (
                  <UserInviteDetails
                    key={user.email}
                    onDelete={() => {
                      if (user.status === 'pending') {
                        void handleRemoveUser(user);

                        return;
                      }

                      prompt.confirm({
                        title: 'Remove user',
                        message: 'Are you sure you want to remove this user?',
                        onConfirm: (answer) => {
                          if (answer) {
                            void handleRemoveUser(user);
                          }
                        }
                      });
                    }}
                    onResend={() => {
                      void verifiedAction.run(() => handleResendInvite(user));
                    }}
                    onUpdate={(update: Partial<User>) => {
                      handleUpdateUser(update, user);
                    }}
                    user={user}
                  />
                ))}
            </div>
          </Content>
          <Content value='guests'>
            <GuestsTab
              guests={guests.filter(
                (guest) => guest.status === 'accepted' && !isSupportUser(guest)
              )}
              onDelete={(id: string) => () => {
                prompt.confirm({
                  title: 'Remove guest',
                  message: 'Are you sure you want to remove this user?',
                  onConfirm: (answer) => {
                    if (answer) {
                      setUsers((draft) => draft.filter((u) => u.id !== id));
                    }
                  }
                });
              }}
              onResend={(id: string) => () => {
                const user = users.find((u) => u.id === id);

                void verifiedAction.run(() => handleResendInvite(user!));
              }}
              updateGuest={(id, update) => {
                setUsers((draft) => draft.map((u) => (u.id === id ? { ...u, ...update } : u)));
              }}
            />
          </Content>
        </div>
        <InviteSupport
          organizationId={organizationId}
          roomId={roomId}
          supportUser={supportUser}
          onRevoke={handleRemoveUser}
        />
      </Root>
      {verifiedAction.prompt}
    </div>
  );
};
